export const MAIN_APP_HIDDEN_ROUTES = new Set([
  "/my-tasks",
  "/reports",
  // SERV-3225: "/clients" is deliberately absent. The ServEngine level lists
  // every entity's clients with an Entity column (the backend already widens
  // /client/list when the session is not an entity login).
  "/locations",
  "/assets",
]);

// SERV-3137: hidden under the same rule Dispatch Queue follows (it is not in
// MAIN_APP_HIDDEN_ROUTES or ENTITY_HIDDEN_ROUTES either, so only this set applies).
export const BARE_BONES_HIDDEN_ROUTES = new Set(["/sp-calendar", "/dispatch-queue", "/live-map"]);

/**
 * SERV-2525: W-9 requests do not exist at entity level. SERV-3226: neither do
 * reactive hire requests, and "entity level" includes a main-app super admin who
 * picked an entity in the label menu (see isInsideEntity), not only an
 * entity-subdomain login.
 *
 * Matched on a nav item's full `to`, unlike the sets above, because these routes
 * nest under a parent the entity IS allowed. Matching the first segment would
 * take "/service-providers" down with them. Entries must be lowercase, see
 * isEntityHiddenRoute.
 */
export const ENTITY_HIDDEN_ROUTES = new Set([
  "/service-providers/w9-requests",
  "/service-providers/reactive-hire-requests",
]);

/**
 * Route-guard counterpart to ENTITY_HIDDEN_ROUTES. A Set lookup on the raw
 * pathname is not enough to close the route off, because the router registers
 * each of these both with and without a trailing slash, and matches paths
 * case-insensitively (`createRouter` sets no `caseSensitive`), so an untouched
 * "/Service-Providers/W9-Requests" would render the page past an exact compare.
 */
export const isEntityHiddenRoute = (pathname: string): boolean => {
  const normalized = pathname.replace(/\/+$/, "").toLowerCase() || "/";

  for (const hidden of ENTITY_HIDDEN_ROUTES) {
    if (normalized === hidden || normalized.startsWith(`${hidden}/`)) return true;
  }

  return false;
};

export const ENTITY_ACCESS_ROUTE_KEYS = new Set([
  "/accounts-payable",
  "/accounts-payable/sp-payment-methods",
  "/close-out",
  "/check-in-out",
  "/accounts-receivable",
  "/email-ai",
  "/clients",
  "/chat",
  "/sp-calendar",
  "/locations",
  "/work-orders",
  "/roles-permissions",
  "/user-management",
  "/service-providers",
  "/procurement",
  "/procurement/overview",
  "/procurement/pricing",
  "/procurement/returns",
  "/procurement/accounts",
  // SERV-2663: a super admin inside an entity manages that entity's storage units too.
  "/procurement/storage-units",
  "/procurement/calendar",
  "/proposals",
  "/files",
  "/form-builder",
  "/form-builder/templates",
  "/form-builder/assignments",
  // SERV-2525: hidden at entity level. Kept commented rather than deleted so it
  // can be restored, but restoring it ALONE does nothing: the sidebar drops the
  // item again via ENTITY_HIDDEN_ROUTES above, which must be reverted too.
  // "/service-providers/w9-requests",
]);

/**
 * SERV-3232: screens that do not exist in an app-level user's merged
 * ("ServEngine") view. Each is either a create with no owning-entity picker
 * yet (the backend refuses the write until an entity is picked) or a list the
 * backend has not converted to the request scope (it answers empty there).
 * The set shrinks as creates gain a picker and lists convert; the mechanism
 * stays so a later screen can opt out the same way. The Clients and Service
 * Providers lists are served at ServEngine level (the provider list is global,
 * the client list reads the request scope and names each row's entity), so
 * only their creates and the two provider queues, which have not converted,
 * stay here. Matched on the full `to`, like ENTITY_HIDDEN_ROUTES, because the
 * converted lists share a parent with entries here. Entries must be
 * lowercase, see isMergedViewHiddenRoute.
 */
export const MERGED_VIEW_HIDDEN_ROUTES = new Set([
  "/proposals/manage",
  "/procurement/overview",
  "/procurement/pricing",
  "/procurement/returns",
  "/procurement/accounts",
  "/procurement/storage-units",
  "/accounts-payable/manage",
  "/accounts-payable/sp-payment-methods",
  "/user-management",
  "/clients/add-new-client",
  "/service-providers/onboarding",
  "/service-providers/w9-requests",
  "/service-providers/reactive-hire-requests",
  "/files",
  "/form-builder",
  "/form-builder/templates",
  "/form-manager",
  "/settings",
  "/roles-permissions",
  "/entities",
]);

export const isMergedViewHiddenRoute = (pathname: string): boolean => {
  const normalized = pathname.replace(/\/+$/, "").toLowerCase() || "/";

  for (const hidden of MERGED_VIEW_HIDDEN_ROUTES) {
    if (normalized === hidden || normalized.startsWith(`${hidden}/`)) return true;
  }

  return false;
};

/**
 * SERV-3232: screens of the modules the roles matrix (SERV-3229 draft,
 * version 2026-10-15-draft-r2) grants only at entity level. A module that no
 * ServEngine-level role holds, only entity-level roles plus Entity Lead and
 * Super User, is an entity screen: an app-level user gets it once she picks an
 * entity and not in the merged view, where it has nothing to show. A super
 * admin is not an app-level user and keeps MAIN_APP_HIDDEN_ROUTES as before.
 * client.brands is entity-only too but is a tab of the client record, not a
 * route; dispatch_queue is entity-only but BARE_BONES_HIDDEN_ROUTES closes it
 * for everyone. Matched on the first segment, like MAIN_APP_HIDDEN_ROUTES, so
 * a record under one (/locations/:id) follows it.
 *
 * Derived from onengine-backend prisma/seed-data/roles-matrix.json; regenerate
 * it whenever that matrix changes. /check-in-out is the exception: SERV-3380
 * serves its queue across entities, so it stays open in the merged view.
 */
export const ENTITY_LEVEL_ROUTES = new Set(["/email-ai", "/locations"]);

export const isEntityLevelRoute = (pathname: string): boolean => {
  const [firstSegment] = pathname.toLowerCase().split("/").filter(Boolean);
  return firstSegment !== undefined && ENTITY_LEVEL_ROUTES.has(`/${firstSegment}`);
};
